import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, LogOut, Plus, Trash2, Upload, X } from "lucide-react";
import api, { imgSrc, formatApiErrorDetail } from "@/lib/api";
import AdminLogin from "./AdminLogin";
import Logo from "@/components/Logo";
import { toast } from "sonner";

/* ---------------- image field ---------------- */
function ImageField({ label, value, onChange, testId }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      <label className="field-label">{label}</label>
      <div className="flex items-start gap-3">
        {value ? (
          <div className="photo-frame h-16 w-24 shrink-0 rounded-md">
            <img src={imgSrc(value)} alt="" />
          </div>
        ) : null}
        <div className="flex-1 space-y-2">
          <input className="field !py-2 text-xs" value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Image URL or upload" data-testid={testId} />
          <div className="flex gap-2">
            <button type="button" onClick={() => ref.current.click()} disabled={busy} className="inline-flex items-center gap-1.5 rounded-full border border-charcoal/20 px-4 py-1.5 text-xs font-medium hover:border-charcoal">
              {busy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} Upload
            </button>
            {value ? (
              <button type="button" onClick={() => onChange("")} className="rounded-full px-3 py-1.5 text-xs text-charcoal/45 hover:text-red-700">Clear</button>
            ) : null}
          </div>
        </div>
      </div>
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
    </div>
  );
}

function MultiImages({ label, value = [], onChange }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (files) => {
    setBusy(true);
    try {
      const urls = [];
      for (const f of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/admin/upload", fd);
        urls.push(data.url);
      }
      onChange([...value, ...urls]);
      toast.success(`${urls.length} photo${urls.length > 1 ? "s" : ""} added`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      <label className="field-label">{label}</label>
      <div className="flex flex-wrap gap-2">
        {value.map((u, i) => (
          <div key={`${u}-${i}`} className="photo-frame relative h-16 w-24 rounded-md">
            <img src={imgSrc(u)} alt="" />
            <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-red-700 text-white" data-testid="gallery-photo-remove">
              <X size={11} />
            </button>
          </div>
        ))}
        <button type="button" onClick={() => ref.current.click()} disabled={busy} className="flex h-16 w-24 flex-col items-center justify-center gap-1 rounded-md border border-dashed border-charcoal/25 text-xs text-charcoal/50 hover:border-charcoal/60">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <><Upload size={14} /> Add</>}
        </button>
      </div>
      <input ref={ref} type="file" accept="image/*" multiple className="hidden" onChange={(e) => upload(e.target.files)} />
    </div>
  );
}

/* ---------------- content schema ---------------- */
const SECTIONS = [
  { key: "hero", label: "Hero", fields: [["eyebrow", "Eyebrow label", "input"], ["line1", "Headline — line 1", "input"], ["line2", "Headline — line 2", "input"], ["sub", "Sub headline", "textarea"], ["image", "Hero photo", "image"]] },
  { key: "philosophy", label: "Philosophy", fields: [["eyebrow", "Eyebrow label", "input"], ["title", "Heading", "input"], ["body", "Paragraph", "textarea"], ["signature", "Signature", "input"], ["image", "Photo", "image"]] },
  { key: "work_intro", label: "Our Work intro", fields: [["eyebrow", "Eyebrow label", "input"], ["title", "Heading", "input"], ["sub", "Sub text", "textarea"]] },
  { key: "before_after", label: "Before & After", fields: [["title", "Heading", "input"], ["caption", "Caption", "textarea"], ["before_label", "Left label", "input"], ["after_label", "Right label", "input"], ["before_image", "First photo (frame)", "image"], ["after_image", "Second photo (finished)", "image"]] },
  { key: "contact", label: "Contact details", fields: [["phone", "Phone", "input"], ["email", "Email", "input"], ["note", "Response note", "textarea"], ["form_intro", "Form intro", "textarea"]] },
  { key: "about", label: "About page", custom: "about" },
  { key: "marquee", label: "Marquee", custom: "marquee" },
  { key: "process", label: "Process steps", type: "repeat", item: [["n", "No.", "input"], ["title", "Title", "input"], ["desc", "Description", "textarea"]] },
  { key: "services", label: "Services", type: "repeat", item: [["num", "No.", "input"], ["title", "Title", "input"], ["tagline", "Tagline", "input"], ["description", "Description", "textarea"], ["features", "Features (one per line)", "lines"], ["image", "Photo", "image"]] },
];

const STATUS_LABELS = { new: "New", in_progress: "In progress", quoted: "Quoted", archived: "Archived" };

/* ---------------- enquiries tab ---------------- */
function Enquiries() {
  const qc = useQueryClient();
  const { data: rows, isLoading } = useQuery({ queryKey: ["admin-enquiries"], queryFn: async () => (await api.get("/admin/enquiries")).data });

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/enquiries/${id}`, { status });
      qc.invalidateQueries({ queryKey: ["admin-enquiries"] });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this enquiry?")) return;
    await api.delete(`/admin/enquiries/${id}`);
    qc.invalidateQueries({ queryKey: ["admin-enquiries"] });
  };

  if (isLoading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-ivory/50" /></div>;
  if (!rows?.length) return <p className="py-16 text-center text-sm text-ivory/50">No enquiries yet — they will appear here as soon as a quote request comes through.</p>;

  return (
    <div className="overflow-x-auto rounded-lg border border-ivory/15">
      <table className="admin-table w-full min-w-[860px]" data-testid="admin-enquiries-table">
        <thead className="bg-ivory/5">
          <tr><th>Received</th><th>Name</th><th>Contact</th><th>Service</th><th>Suburb</th><th>Budget / start</th><th>Notes</th><th>Status</th><th></th></tr>
        </thead>
        <tbody className="divide-y divide-ivory/10">
          {rows.map((r) => (
            <tr key={r.id} data-testid="admin-quote-row">
              <td className="whitespace-nowrap text-xs">{new Date(r.created_at).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}<br /><span className="text-ivory/40">{new Date(r.created_at).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" })}</span></td>
              <td className="font-medium text-ivory">{r.name}</td>
              <td className="text-xs"><a className="block hover:text-gold-light" href={`tel:${r.phone}`}>{r.phone}</a><a className="block hover:text-gold-light" href={`mailto:${r.email}`}>{r.email}</a></td>
              <td className="text-xs">{r.service || "—"}</td>
              <td className="text-xs">{r.suburb || "—"}</td>
              <td className="text-xs">{r.budget || "—"}<br /><span className="text-ivory/40">{r.start_date || ""}</span></td>
              <td className="max-w-[280px] text-xs leading-relaxed text-ivory/70">{r.message}</td>
              <td>
                <select value={r.status} onChange={(e) => setStatus(r.id, e.target.value)} className="rounded border border-ivory/25 bg-transparent px-2 py-1.5 text-xs text-ivory" data-testid="admin-status-select">
                  {Object.entries(STATUS_LABELS).map(([v, l]) => <option key={v} value={v} className="text-charcoal">{l}</option>)}
                </select>
              </td>
              <td>
                <button onClick={() => remove(r.id)} className="rounded p-1.5 text-ivory/40 hover:text-red-400" aria-label="Delete enquiry" data-testid="admin-delete-enquiry"><Trash2 size={14} /></button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ---------------- content tab ---------------- */
function SectionFields({ fields, value, onChange }) {
  return (
    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
      {fields.map(([path, label, type]) => {
        const set = (v) => onChange({ ...value, [path]: v });
        if (type === "image") return (
          <div key={path} className="sm:col-span-2">
            <ImageField label={label} value={value[path]} onChange={set} testId={`content-${path}-input`} />
          </div>
        );
        if (type === "lines") return (
          <div key={path} className="sm:col-span-2">
            <label className="field-label">{label}</label>
            <textarea rows={4} className="field resize-none" value={(value[path] || []).join("\n")} onChange={(e) => set(e.target.value.split("\n"))} data-testid={`content-${path}-input`} />
          </div>
        );
        if (type === "textarea") return (
          <div key={path} className="sm:col-span-2">
            <label className="field-label">{label}</label>
            <textarea rows={3} className="field resize-none" value={value[path] || ""} onChange={(e) => set(e.target.value)} data-testid={`content-${path}-input`} />
          </div>
        );
        return (
          <div key={path}>
            <label className="field-label">{label}</label>
            <input className="field" value={value[path] || ""} onChange={(e) => set(e.target.value)} data-testid={`content-${path}-input`} />
          </div>
        );
      })}
    </div>
  );
}

function AboutEditor({ value, onChange }) {
  const [story, setStory] = useState((value.story || []).join("\n\n"));
  const [areas, setAreas] = useState((value.areas || []).join("\n"));
  const [values, setValues] = useState((value.values || []).map((v) => `${v.title} | ${v.desc}`).join("\n"));
  useEffect(() => {
    onChange({
      ...value,
      story: story.split(/\n\s*\n/).map((s) => s.trim()).filter(Boolean),
      areas: areas.split("\n").map((s) => s.trim()).filter(Boolean),
      values: values.split("\n").filter(Boolean).map((l) => { const [t, ...rest] = l.split("|"); return { title: t.trim(), desc: rest.join("|").trim() }; }),
    });
  }, [story, areas, values]);
  return (
    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
      <div className="sm:col-span-2">
        <label className="field-label">Heading</label>
        <input className="field" value={value.title || ""} onChange={(e) => onChange({ ...value, title: e.target.value })} data-testid="content-about-title" />
      </div>
      <div className="sm:col-span-2">
        <label className="field-label">Story — paragraphs separated by a blank line</label>
        <textarea rows={6} className="field resize-none" value={story} onChange={(e) => setStory(e.target.value)} />
      </div>
      <div>
        <label className="field-label">Suburbs — one per line</label>
        <textarea rows={5} className="field resize-none" value={areas} onChange={(e) => setAreas(e.target.value)} />
      </div>
      <div>
        <label className="field-label">Values — one per line: Title | Description</label>
        <textarea rows={5} className="field resize-none" value={values} onChange={(e) => setValues(e.target.value)} />
      </div>
      <div className="sm:col-span-2">
        <ImageField label="Story photo" value={value.image} onChange={(v) => onChange({ ...value, image: v })} />
      </div>
    </div>
  );
}

function MarqueeEditor({ value, onChange }) {
  return (
    <div>
      <label className="field-label">Items — one per line</label>
      <textarea rows={6} className="field resize-none" value={(value || []).join("\n")} onChange={(e) => onChange(e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))} />
    </div>
  );
}

function ContentTab() {
  const qc = useQueryClient();
  const { data: content } = useQuery({ queryKey: ["content-admin"], queryFn: async () => (await api.get("/content")).data });
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    if (content) setDrafts(content);
  }, [content]);

  const saveSection = async (key) => {
    setSaving(key);
    try {
      await api.put("/admin/content", { updates: { [key]: drafts[key] } });
      toast.success("Saved — the website is updated");
      qc.invalidateQueries({ queryKey: ["content"] });
      qc.invalidateQueries({ queryKey: ["content-admin"] });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(null);
    }
  };

  if (!content) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-ivory/50" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-content-tab">
      {SECTIONS.map((s) => {
        const draft = drafts[s.key];
        if (draft === undefined) return null;
        return (
          <div key={s.key} className="rounded-lg border border-ivory/15 bg-ivory/5 p-6">
            <div className="mb-5 flex items-center justify-between">
              <h3 className="font-serif text-xl text-ivory">{s.label}</h3>
              <button onClick={() => saveSection(s.key)} disabled={saving === s.key} className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2 text-xs font-semibold text-charcoal hover:bg-gold-light disabled:opacity-60" data-testid={`content-save-${s.key}`}>
                {saving === s.key ? <Loader2 size={12} className="animate-spin" /> : null} Save
              </button>
            </div>
            {s.custom === "about" ? <AboutEditor value={draft} onChange={(v) => setDrafts((d) => ({ ...d, [s.key]: v }))} /> :
              s.custom === "marquee" ? <MarqueeEditor value={draft} onChange={(v) => setDrafts((d) => ({ ...d, [s.key]: v }))} /> :
              s.type === "repeat" ? (
                <div className="space-y-5">
                  {draft.map((item, i) => (
                    <div key={i} className="rounded-md border border-ivory/15 bg-charcoal/40 p-5">
                      <p className="mb-4 font-mono text-[10px] uppercase tracking-[0.2em] text-gold-light">{s.label} {i + 1}</p>
                      <SectionFields fields={s.item} value={item} onChange={(v) => setDrafts((d) => ({ ...d, [s.key]: d[s.key].map((it, j) => (j === i ? v : it)) }))} />
                    </div>
                  ))}
                </div>
              ) : <SectionFields fields={s.fields} value={draft} onChange={(v) => setDrafts((d) => ({ ...d, [s.key]: v }))} />}
          </div>
        );
      })}
    </div>
  );
}

/* ---------------- projects tab ---------------- */
const EMPTY_PROJECT = { title: "", location: "", category: "Decks & Verandahs", description: "", timber: "", cover_image: "", gallery: [], before_image: "", after_image: "", featured: false, order: 99 };
const CATEGORIES = ["Decks & Verandahs", "Structural Carpentry", "Timber Framing", "Architectural Finishes"];

function ProjectEditor({ project, onClose }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(project ? { ...project } : { ...EMPTY_PROJECT });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.title.trim()) return toast.error("A title is needed");
    setBusy(true);
    try {
      if (project) await api.put(`/admin/projects/${project.id}`, form);
      else await api.post("/admin/projects", form);
      qc.invalidateQueries({ queryKey: ["projects"] });
      toast.success("Project saved — live on the website");
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-charcoal/70 p-4 backdrop-blur-sm sm:p-8" data-testid="admin-project-editor">
      <div className="my-8 w-full max-w-2xl rounded-lg bg-ivory p-6 sm:p-9">
        <div className="mb-6 flex items-center justify-between">
          <h3 className="font-serif text-2xl text-charcoal">{project ? "Edit project" : "New project"}</h3>
          <button onClick={onClose} className="rounded-full p-2 hover:bg-charcoal/5" aria-label="Close"><X size={18} /></button>
        </div>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div><label className="field-label">Title *</label><input className="field" value={form.title} onChange={set("title")} data-testid="project-title-input" /></div>
          <div><label className="field-label">Suburb / location (optional)</label><input className="field" value={form.location} onChange={set("location")} /></div>
          <div>
            <label className="field-label">Category</label>
            <select className="field" value={form.category} onChange={set("category")}>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div><label className="field-label">Timber / materials</label><input className="field" value={form.timber} onChange={set("timber")} /></div>
          <div className="sm:col-span-2"><label className="field-label">Description</label><textarea rows={3} className="field resize-none" value={form.description} onChange={set("description")} /></div>
          <div className="sm:col-span-2"><ImageField label="Cover photo" value={form.cover_image} onChange={(v) => setForm((f) => ({ ...f, cover_image: v }))} testId="project-cover-input" /></div>
          <div className="sm:col-span-2"><MultiImages label="Gallery photos" value={form.gallery} onChange={(v) => setForm((f) => ({ ...f, gallery: v }))} /></div>
          <ImageField label="Before photo (optional)" value={form.before_image} onChange={(v) => setForm((f) => ({ ...f, before_image: v }))} />
          <ImageField label="After photo (optional)" value={form.after_image} onChange={(v) => setForm((f) => ({ ...f, after_image: v }))} />
          <label className="flex items-center gap-2 text-sm text-charcoal/70">
            <input type="checkbox" checked={form.featured} onChange={(e) => setForm((f) => ({ ...f, featured: e.target.checked }))} data-testid="project-featured-toggle" className="h-4 w-4 accent-[#C29B38]" />
            Feature on the home page
          </label>
          <div><label className="field-label">Sort order</label><input type="number" className="field" value={form.order} onChange={set("order")} /></div>
        </div>
        <div className="mt-8 flex gap-3">
          <button onClick={save} disabled={busy} className="btn-dark flex-1 disabled:opacity-60" data-testid="project-save-button">
            {busy ? <Loader2 size={15} className="animate-spin" /> : null} Save project
          </button>
          <button onClick={onClose} className="btn-ghost">Cancel</button>
        </div>
      </div>
    </div>
  );
}

function ProjectsTab() {
  const qc = useQueryClient();
  const { data: projects } = useQuery({ queryKey: ["projects"], queryFn: async () => (await api.get("/projects")).data });
  const [editing, setEditing] = useState(undefined);

  const remove = async (p) => {
    if (!window.confirm(`Remove "${p.title}" from the website?`)) return;
    await api.delete(`/admin/projects/${p.id}`);
    qc.invalidateQueries({ queryKey: ["projects"] });
    toast.success("Project removed");
  };

  return (
    <div data-testid="admin-projects-tab">
      <div className="mb-6 flex items-center justify-between">
        <p className="text-sm text-ivory/60">{projects?.length || 0} projects on the website</p>
        <button onClick={() => setEditing(null)} className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-xs font-semibold text-charcoal hover:bg-gold-light" data-testid="admin-add-project">
          <Plus size={14} /> Add project
        </button>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {(projects || []).map((p) => (
          <div key={p.id} className="overflow-hidden rounded-lg border border-ivory/15 bg-ivory/5" data-testid="admin-project-card">
            <div className="photo-frame aspect-[16/10]">
              <img src={imgSrc(p.cover_image)} alt={p.title} />
            </div>
            <div className="p-4">
              <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-gold-light">{p.category}</p>
              <h4 className="mt-1 font-serif text-lg text-ivory">{p.title}</h4>
              {p.featured && <p className="mt-1 text-[10px] uppercase tracking-widest text-ivory/40">Featured</p>}
              <div className="mt-3 flex gap-2">
                <button onClick={() => setEditing(p)} className="rounded-full border border-ivory/25 px-4 py-1.5 text-xs text-ivory hover:border-ivory" data-testid="admin-edit-project">Edit</button>
                <button onClick={() => remove(p)} className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs text-ivory/45 hover:text-red-400" data-testid="admin-delete-project"><Trash2 size={12} /> Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {editing !== undefined && (
        <ProjectEditor project={editing} onClose={() => setEditing(undefined)} />
      )}
    </div>
  );
}

/* ---------------- shell ---------------- */
function Dashboard({ user }) {
  const [tab, setTab] = useState("enquiries");
  const logout = async () => {
    await api.post("/auth/logout");
    window.location.href = "/";
  };
  return (
    <div className="min-h-screen bg-charcoal text-ivory" data-testid="admin-dashboard">
      <header className="border-b border-ivory/10">
        <div className="wrap flex h-16 items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo size="h-10" onDark />
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ivory/50">Owner Dashboard</span>
          </div>
          <button onClick={logout} className="inline-flex items-center gap-2 rounded-full border border-ivory/25 px-4 py-2 text-xs hover:border-ivory" data-testid="admin-logout">
            <LogOut size={13} /> Sign out
          </button>
        </div>
      </header>
      <div className="wrap py-10">
        <h1 className="font-serif text-3xl">Welcome back, {user?.name || "Lucas"}.</h1>
        <p className="mt-1 text-sm text-ivory/50">Quote enquiries, website content and your project portfolio — all in one place.</p>
        <div className="mt-8 flex gap-2">
          {[["enquiries", "Quote enquiries"], ["content", "Website content"], ["projects", "Project portfolio"]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} className={`rounded-full px-5 py-2.5 text-xs font-semibold tracking-wide transition-colors ${tab === k ? "bg-ivory text-charcoal" : "border border-ivory/25 text-ivory/60 hover:text-ivory"}`} data-testid={`admin-tab-${k}`}>
              {l}
            </button>
          ))}
        </div>
        <div className="mt-8">
          {tab === "enquiries" && <Enquiries />}
          {tab === "content" && <ContentTab />}
          {tab === "projects" && <ProjectsTab />}
        </div>
      </div>
    </div>
  );
}

export default function Admin() {
  const { data, isLoading } = useQuery({ queryKey: ["me"], queryFn: async () => (await api.get("/auth/me")).data, retry: false, staleTime: 5 * 60 * 1000 });
  if (isLoading) return <div className="flex min-h-screen items-center justify-center bg-charcoal"><Loader2 className="animate-spin text-ivory/40" /></div>;
  return data ? <Dashboard user={data} /> : <AdminLogin />;
}
